import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import SearchIcon from '@mui/icons-material/Search';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import api, { truckService } from '../api/api';
import { formatPortalDate } from '../utils/dateFormat';
import { InspectionReportDialog } from './TruckInspectionLogs';

function isResolved(inspection) {
  return ['pass', 'resolved'].includes(String(inspection?.status || '').toLowerCase())
    || inspection?.details?.defect_resolved === true;
}

function statusDetails(inspection) {
  if (isResolved(inspection)) return { label: 'Resolved', color: '#18734b', background: '#e8f7ef', border: '#a8d8bd' };
  if (inspection?.status === 'fail') return { label: 'Unsafe / Do Not Use', color: '#b42335', background: '#fff0f1', border: '#f1b8c0' };
  return { label: 'Needs Attention', color: '#94610b', background: '#fff6e5', border: '#efd39b' };
}

function searchText(item) {
  const { inspection, truck } = item;
  return [
    inspection?.issues,
    inspection?.inspected_by,
    inspection?.details?.location_of_observation,
    truck?.name,
    truck?.license_plate,
    truck?.make,
    truck?.model,
  ].filter(Boolean).join(' ').toLowerCase();
}

export default function MechanicDefectChecklist() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [view, setView] = useState('open');
  const [updatingId, setUpdatingId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });

  const loadDefects = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const allItems = [];
      const pageSize = 150;
      for (let page = 0; page < 100; page += 1) {
        const response = await api.get('/inspections', {
          params: { type: 'defect_report', page, pageSize, sortBy: 'occurred_at', sortDir: 'desc' },
        });
        const pageItems = Array.isArray(response.data?.items) ? response.data.items : [];
        allItems.push(...pageItems);
        const total = Number(response.data?.total || allItems.length);
        if (allItems.length >= total || pageItems.length < pageSize) break;
      }
      setItems(allItems);
      setError('');
    } catch (exception) {
      setError(exception?.response?.data?.error || 'Unable to load the mechanic defect checklist.');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDefects();
    const refresh = () => document.visibilityState === 'visible' && loadDefects(true);
    const interval = window.setInterval(refresh, 30_000);
    window.addEventListener('focus', refresh);
    window.addEventListener('dataUpdated', refresh);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('dataUpdated', refresh);
    };
  }, [loadDefects]);

  const openCount = useMemo(() => items.filter(({ inspection }) => !isResolved(inspection)).length, [items]);
  const resolvedCount = items.length - openCount;
  const visibleItems = useMemo(() => {
    const term = query.trim().toLowerCase();
    return items
      .filter((item) => !term || searchText(item).includes(term))
      .filter(({ inspection }) => view === 'all' || (view === 'resolved' ? isResolved(inspection) : !isResolved(inspection)))
      .sort((left, right) => {
        const resolutionOrder = Number(isResolved(left.inspection)) - Number(isResolved(right.inspection));
        if (resolutionOrder !== 0) return resolutionOrder;
        return new Date(right.inspection?.occurred_at || 0) - new Date(left.inspection?.occurred_at || 0);
      });
  }, [items, query, view]);

  const markResolved = async (event, inspection) => {
    event.stopPropagation();
    if (isResolved(inspection) || updatingId) return;
    try {
      setUpdatingId(inspection.id);
      const response = await truckService.updateInspection(inspection.truck_id, inspection.id, {
        inspection_type: 'defect_report',
        occurred_at: inspection.occurred_at,
        odometer: inspection.odometer,
        inspected_by: inspection.inspected_by,
        status: 'resolved',
        issues: inspection.issues,
        details: {
          ...(inspection.details || {}),
          defect_resolved: true,
        },
      });
      setItems((current) => current.map((item) => (
        item.inspection.id === inspection.id
          ? { ...item, inspection: response.data }
          : item
      )));
      setToast({
        open: true,
        severity: 'success',
        message: 'Defect resolved. The reporter notification email is being sent automatically.',
      });
      window.dispatchEvent(new Event('dataUpdated'));
    } catch (exception) {
      setToast({
        open: true,
        severity: 'error',
        message: exception?.response?.data?.error || 'Unable to resolve this defect.',
      });
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Paper className="portal-page-header" sx={{ p: { xs: 2, md: 2.5 }, mb: 2.25, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ display: 'grid', placeItems: 'center', width: 46, height: 46, borderRadius: 2, bgcolor: 'rgba(88,80,141,.12)' }}>
              <FactCheckOutlinedIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900 }}>Mechanic Defect Checklist</Typography>
              <Typography variant="body2" color="text.secondary">Review driver-reported defects and check them off when repairs are complete.</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: .75, flexWrap: 'wrap' }}>
            <Chip label={`${openCount} open`} sx={{ bgcolor: '#fff3dc', color: '#8b5b08', fontWeight: 900 }} />
            <Chip label={`${resolvedCount} resolved`} sx={{ bgcolor: '#e8f7ef', color: '#18734b', fontWeight: 900 }} />
          </Box>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 1.25, sm: 1.75 }, mb: 2, borderColor: '#dce5ee' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(260px, 1fr) auto' }, alignItems: 'center', gap: 1.25 }}>
          <TextField
            size="small"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search vehicle, plate, reporter, defect, or location"
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          />
          <ToggleButtonGroup exclusive size="small" value={view} onChange={(_event, next) => next && setView(next)} sx={{ justifySelf: { xs: 'stretch', md: 'end' }, '& .MuiToggleButton-root': { flex: { xs: 1, md: 'initial' }, px: { xs: 1, sm: 2 }, fontWeight: 800 } }}>
            <ToggleButton value="open">Open ({openCount})</ToggleButton>
            <ToggleButton value="all">All ({items.length})</ToggleButton>
            <ToggleButton value="resolved">Resolved ({resolvedCount})</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
          {!visibleItems.length && (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', gridColumn: '1 / -1', borderStyle: 'dashed', borderColor: '#cbd8e5' }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 42, color: '#78a98d', mb: 1 }} />
              <Typography sx={{ fontWeight: 900 }}>No defects found</Typography>
              <Typography variant="body2" color="text.secondary">There are no checklist items matching this view.</Typography>
            </Paper>
          )}
          {visibleItems.map(({ inspection, truck }) => {
            const resolved = isResolved(inspection);
            const badge = statusDetails(inspection);
            const details = inspection.details || {};
            const busy = updatingId === inspection.id;
            return (
              <Paper
                key={inspection.id}
                role="button"
                tabIndex={0}
                variant="outlined"
                onClick={() => setSelected(inspection)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setSelected(inspection);
                  }
                }}
                sx={{
                  p: { xs: 1.25, sm: 1.6 },
                  cursor: 'pointer',
                  borderColor: badge.border,
                  borderLeft: `5px solid ${badge.color}`,
                  background: resolved ? 'linear-gradient(135deg, #effaf4 0%, #ffffff 82%)' : 'linear-gradient(135deg, #ffffff 0%, #fbfdff 100%)',
                  transition: 'transform 160ms ease, box-shadow 160ms ease',
                  '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 8px 24px rgba(16,42,67,.09)' },
                  '&:focus-visible': { outline: '2px solid #655b8c', outlineOffset: 2 },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.1 }}>
                  <Checkbox
                    checked={resolved}
                    disabled={resolved || busy}
                    onClick={(event) => event.stopPropagation()}
                    onChange={(event) => markResolved(event, inspection)}
                    inputProps={{ 'aria-label': `Mark defect for ${truck?.name || 'vehicle'} resolved` }}
                    sx={{ mt: -.65, ml: -.65, color: badge.color, '&.Mui-checked': { color: '#18734b' } }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                      <Box>
                        <Typography sx={{ fontWeight: 950, color: '#16324f', fontSize: { xs: '1rem', sm: '1.08rem' } }}>
                          {truck?.name || `Vehicle #${inspection.truck_id}`}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6b7f91', fontWeight: 700 }}>
                          {[truck?.license_plate, [truck?.year, truck?.make, truck?.model].filter(Boolean).join(' ')].filter(Boolean).join(' • ')}
                        </Typography>
                      </Box>
                      <Chip size="small" label={busy ? 'Saving…' : badge.label} sx={{ bgcolor: badge.background, color: badge.color, fontWeight: 900 }} />
                    </Box>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .7, mt: 1, px: 1, py: .7, borderRadius: 1.5, bgcolor: '#edf4fb', border: '1px solid #d2e1ee', color: '#234f73' }}>
                      <ScheduleIcon sx={{ fontSize: 17 }} />
                      <Typography variant="body2" sx={{ fontWeight: 900 }}>
                        Reported: {formatPortalDate(inspection.occurred_at, { includeTime: true })}
                      </Typography>
                    </Box>
                    <Typography sx={{ mt: 1, color: resolved ? '#5b7468' : '#334e68', textDecoration: resolved ? 'line-through' : 'none', whiteSpace: 'pre-wrap' }}>
                      {inspection.issues || 'No defect description provided.'}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: .7, mt: 1.2 }}>
                      <Chip size="small" icon={<PersonOutlineIcon />} label={inspection.inspected_by || 'Reporter not recorded'} variant="outlined" />
                      {details.location_of_observation && <Chip size="small" icon={<DirectionsCarOutlinedIcon />} label={details.location_of_observation} variant="outlined" />}
                    </Box>
                    {resolved && (
                      <Box sx={{ mt: 1.25, p: 1, borderRadius: 1.75, bgcolor: '#e8f7ef', border: '1px solid #b9dfca', color: '#146a45' }}>
                        <Typography variant="body2" sx={{ fontWeight: 900 }}>
                          Resolved{details.defect_resolved_by_name ? ` by ${details.defect_resolved_by_name}` : ''}
                        </Typography>
                        {details.defect_resolved_at && (
                          <Typography variant="caption">{formatPortalDate(details.defect_resolved_at, { includeTime: true })}</Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      <InspectionReportDialog open={Boolean(selected)} inspection={selected} onClose={() => setSelected(null)} />
      <Snackbar open={toast.open} autoHideDuration={5000} onClose={() => setToast((current) => ({ ...current, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast.severity} variant="filled" onClose={() => setToast((current) => ({ ...current, open: false }))}>{toast.message}</Alert>
      </Snackbar>
    </Box>
  );
}
